/**
 * StatusPill — the professional soft-badge status chip for the stakeholder portal.
 * Replaces the old emoji + label span. Renders a Lucide icon + the SHORT rung label
 * inside the pill triad from statusMap (border + bg + text). The full client-facing
 * phrasing and the plain-language blurb stay in the page, not the chip.
 *
 * Takes the derived rung ({ key, label } — a FeedbackClaimLadder is assignable).
 * `key` drives the icon + colour; `label` is only used as the fallback pill text for
 * an unknown key.
 */
import { cn } from '@/lib/utils';
import { getStatusConfig } from '@/lib/portal/statusMap';
import type { FeedbackClaimLadder } from '@/lib/schemas';

interface StatusPillProps {
  rung: Pick<FeedbackClaimLadder, 'key' | 'label'>;
  /** data-testid for the pill span (differs per page: portal-issue-status / your-issues-status). */
  testId?: string;
  className?: string;
  /** Reporter views pass clientPillLabel(rung.key) to override the short label. */
  label?: string;
}

export function StatusPill({ rung, testId, className, label }: StatusPillProps) {
  const config = getStatusConfig(rung.key, rung.label);
  const Icon = config.icon;
  return (
    <span
      data-testid={testId}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium',
        config.pill,
        className,
      )}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden />
      {label ?? config.label}
    </span>
  );
}
